import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Calendar, Clock } from "lucide-react";
import { ReviewDialog } from "../components/ReviewDialog";
import { CancelBookingDialog } from "../components/CancelBookingDialog";
import { CompleteBookingDialog } from "../components/CompleteBookingDialog";
import { ProposeExtraHoursDialog } from "../components/ProposeExtraHoursDialog";

const STATUS_COLORS = {
  pending_payment: "bg-cognac/20 text-cognac",
  confirmed: "bg-sage/20 text-sage",
  completed: "bg-beige text-charcoal",
  cancelled: "bg-destructive/10 text-destructive",
  canceled: "bg-destructive/10 text-destructive",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data));
  }, []);

  const onCompleted = (id) => {
    setBookings((prev) => prev.map((b) => b.booking_id === id ? { ...b, status: "completed" } : b));
  };

  const onCanceled = (id) => {
    setBookings((prev) => prev.map((b) => b.booking_id === id ? { ...b, status: "canceled" } : b));
  };

  const onExtraHoursProposed = (id, hours) => {
    setBookings((prev) => prev.map((b) => b.booking_id === id ? { ...b, proposed_extra_hours: hours } : b));
  };

  const markReviewed = (id) => {
    setBookings((prev) => prev.map((b) => b.booking_id === id ? { ...b, has_review: true } : b));
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl lg:text-5xl text-charcoal">Hi, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-charcoal/70 mt-2">
              {user?.role === "handyman" ? "Your incoming jobs" : "Your bookings"}
            </p>
          </div>
          {user?.role === "student" && (
            <Link to="/browse">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white btn-lift" data-testid="new-booking-btn">
                Book another Move-in Pro
              </Button>
            </Link>
          )}
        </div>

        <div className="mt-10 space-y-4">
          {bookings.length === 0 && (
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-10 text-center" data-testid="no-bookings">
              <p className="text-charcoal/60">No bookings yet.</p>
              {user?.role === "student" && <Link to="/browse" className="text-terracotta hover:underline mt-2 inline-block">Browse Move-in Pros →</Link>}
            </div>
          )}
          {bookings.map((b) => (
            <div key={b.booking_id} data-testid={`booking-${b.booking_id}`}
              className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 card-hover">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-xl text-charcoal">
                    {user?.role === "student" ? `with ${b.handyman_name}` : `with ${b.student_name}`}
                  </h3>
                  <Badge className={`rounded-full ${STATUS_COLORS[b.status] || "bg-beige"}`}>{b.status.replace("_", " ")}</Badge>
                </div>
                <p className="text-charcoal/70 mt-2">{b.description}</p>
                {b.addons && b.addons.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`booking-addons-${b.booking_id}`}>
                    {b.addons.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-terracotta/10 text-terracotta text-xs px-2.5 py-0.5">
                        + {a.label} · ${Number(a.price).toFixed(0)}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 text-sm text-charcoal/60">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(b.scheduled_at).toLocaleString()}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {b.estimated_hours}h</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-display text-terracotta">${b.amount}</p>
                <div className="flex gap-2 mt-2 justify-end items-center">
                  <Link to={`/messages/${user?.role === "student" ? b.handyman_id : b.student_id}`}>
                    <Button variant="outline" size="sm" className="rounded-full" data-testid={`msg-${b.booking_id}`}>Message</Button>
                  </Link>
                  {b.status === "confirmed" && user?.role === "student" && (
                    <>
                      <CancelBookingDialog booking={b} onCanceled={onCanceled} />
                      <CompleteBookingDialog booking={b} onCompleted={onCompleted} />
                    </>
                  )}
                  {b.status === "confirmed" && user?.role === "handyman" && (
                    <ProposeExtraHoursDialog booking={b} onProposed={onExtraHoursProposed} />
                  )}
                  {b.status === "pending_payment" && user?.role === "student" && (
                    <CancelBookingDialog booking={b} onCanceled={onCanceled} />
                  )}
                  {b.status === "completed" && user?.role === "student" && !b.has_review && (
                    <ReviewDialog booking={b} onReviewed={markReviewed} />
                  )}
                  {b.status === "completed" && user?.role === "student" && b.has_review && (
                    <Badge className="rounded-full bg-sage/20 text-sage" data-testid={`reviewed-${b.booking_id}`}>Reviewed</Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
